<template>
  <v-dialog v-model="showDialog" max-width="600">
    <v-card>
      <v-card-title>Edit Item</v-card-title>
      <v-card-text>
        <v-form ref="itemForm" v-model="itemValid" @submit.prevent="saveItem">
          <v-text-field
            v-model="editItemData.name"
            label="Name"
            :rules="[v => !!v || 'Name is required']"
            required
            @keydown.enter.prevent="saveItem"
          ></v-text-field>
          
          <!-- Collapsible Description -->
          <div class="mb-3">
            <v-btn
              variant="text"
              size="small"
              @click="showDescription = !showDescription"
              class="px-0"
            >
              <v-icon>{{ showDescription ? 'mdi-chevron-up' : 'mdi-chevron-down' }}</v-icon>
              Description (optional)
            </v-btn>
            <v-textarea
              v-if="showDescription"
              v-model="editItemData.description"
              label="Description"
              rows="2"
              class="mt-2"
              @keydown.enter.prevent="saveItem"
            ></v-textarea>
          </div>
          
          <v-text-field
            v-model.number="editItemData.quantity"
            :label="editItemData.quantity_type === 'fixed' ? 'Quantity' : 'Quantity per period'"
            type="number"
            min="1"
            :rules="quantityRules"
            required
            @keydown.enter.prevent="saveItem"
          ></v-text-field>
          
          <v-radio-group
            v-model="editItemData.quantity_type"
            inline
            class="mt-2"
          >
            <v-radio label="Fixed quantity" value="fixed"></v-radio>
            <v-radio label="Per days" value="per_days"></v-radio>
          </v-radio-group>
          
          <v-text-field
            v-if="editItemData.quantity_type === 'per_days'"
            v-model.number="editItemData.per_days"
            label="Every X days"
            type="number"
            min="1"
            :rules="[v => !!v || 'Days is required', v => v >= 1 || 'Days must be at least 1']"
            required
            hint="e.g., 1 pair of pants every 3 days"
            @keydown.enter.prevent="saveItem"
          ></v-text-field>
          
          <v-combobox
            v-model="selectedGroup"
            :items="groupOptions"
            item-title="name"
            item-value="id"
            label="Group (optional)"
            hint="Select existing group or type new group name"
            clearable
            @update:search="newGroupName = $event"
          >
            <template #no-data>
              <v-list-item>
                <v-list-item-title>
                  Press enter to create "<strong>{{ newGroupName }}</strong>"
                </v-list-item-title>
              </v-list-item>
            </template>
          </v-combobox>
        </v-form>
      </v-card-text>
      <v-card-actions>
        <v-spacer></v-spacer>
        <v-btn @click="cancelEdit" :disabled="isSaving">Cancel</v-btn>
        <v-btn 
          color="primary" 
          @click="saveItem" 
          :disabled="!itemValid"
          :loading="isSaving"
        >
          Update
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import { ref, computed, watch } from 'vue'
import { useListStore } from '../stores/list'
import { useGroupStore } from '../stores/group'
import axios from 'axios'

export default {
  name: 'EditItemDialog',
  props: {
    modelValue: {
      type: Boolean,
      default: false
    },
    itemData: {
      type: Object,
      default: null
    },
    shared: {
      type: Boolean,
      default: false
    },
    linkId: {
      type: String,
      default: null
    },
    hmac: {
      type: String,
      default: null
    },
    authenticatedPassword: {
      type: String,
      default: null
    },
    groupOptions: {
      type: Array,
      default: () => []
    }
  },
  emits: ['update:modelValue', 'updated', 'message'],
  setup(props, { emit }) {
    const listStore = useListStore()
    const groupStore = useGroupStore()
    
    const itemForm = ref(null)
    const itemValid = ref(false)
    const isSaving = ref(false)
    const showDescription = ref(false)
    const selectedGroup = ref(null)
    const newGroupName = ref('')
    
    const editItemData = ref({
      name: '',
      description: '',
      quantity: 1,
      quantity_type: 'fixed',
      per_days: null,
      group_id: null
    })
    
    const showDialog = computed({
      get: () => props.modelValue,
      set: (value) => emit('update:modelValue', value)
    })
    
    const quantityRules = [
      v => !!v || 'Quantity is required',
      v => v >= 1 || 'Quantity must be at least 1',
      v => Number.isInteger(v) || 'Quantity must be a whole number'
    ]
    
    const groupOptions = computed(() => {
      if (props.shared) {
        // For shared links, we need to get groups from the parent component
        // We'll pass them as a prop
        return props.groupOptions || []
      }
      return groupStore.groups || []
    })
    
    const showMessage = (message, color = 'success') => {
      emit('message', { message, color })
    }
    
    const saveItem = async () => {
      if (!itemValid.value || !props.itemData) return
      
      isSaving.value = true
      
      try {
        // Handle group creation/selection
        let groupId = null
        
        if (selectedGroup.value) {
          if (typeof selectedGroup.value === 'string') {
            // User typed a new group name
            if (props.shared) {
              // For shared links, we can't create groups through EditItemDialog
              // We should prevent this or handle it differently
              showMessage('Cannot create new groups when editing shared items. Please select an existing group.', 'error')
              return
            } else {
              // For authenticated users, create the group
              const groupResult = await groupStore.createGroup({ 
                name: selectedGroup.value, 
                packlist_id: listStore.currentList.id 
              })
              if (groupResult.success) {
                groupId = groupResult.group.id
                showMessage(`Group "${selectedGroup.value}" created!`, 'info')
              } else {
                showMessage(groupResult.error, 'error')
                return
              }
            }
          } else if (selectedGroup.value.id) {
            // User selected existing group
            groupId = selectedGroup.value.id
          }
        }
        
        const itemData = {
          ...editItemData.value,
          group_id: groupId
        }
        
        if (props.shared) {
          // Update via shared link API
          const params = new URLSearchParams({
            link_id: props.linkId,
            hmac: props.hmac
          })
          if (props.authenticatedPassword) {
            params.append('password', props.authenticatedPassword)
          }
          
          const response = await axios.put(`/api/items/${props.itemData.id}?${params}`, itemData)
          showMessage('Item updated!')
          emit('updated', response.data)
          cancelEdit()
        } else {
          // Update via regular API
          const result = await listStore.updateItem(props.itemData.id, itemData)
          
          if (result.success) {
            showMessage('Item updated!')
            emit('updated', result.item)
            cancelEdit()
          } else {
            showMessage(result.error, 'error')
          }
        }
      } catch (error) {
        showMessage('An error occurred while updating the item', 'error')
      } finally {
        isSaving.value = false
      }
    }
    
    const cancelEdit = () => {
      showDialog.value = false
      showDescription.value = false
    }
    
    // Watch for itemData prop changes to populate the form
    watch(() => props.itemData, (newItemData) => {
      if (newItemData) {
        editItemData.value = {
          name: newItemData.name,
          description: newItemData.description || '',
          quantity: newItemData.quantity || 1,
          quantity_type: newItemData.quantity_type || 'fixed',
          per_days: newItemData.per_days || null,
          group_id: newItemData.group_id || null
        }
        // Set selectedGroup based on context
        if (newItemData.group_id) {
          if (props.shared) {
            // For shared links, find the group in groupOptions
            selectedGroup.value = props.groupOptions.find(g => g.id === newItemData.group_id) || null
          } else {
            // For authenticated users, use groupStore
            selectedGroup.value = groupStore.getGroupById(newItemData.group_id)
          }
        } else {
          selectedGroup.value = null
        }
        showDescription.value = !!(newItemData.description && newItemData.description.trim())
      }
    }, { immediate: true })
    
    return {
      showDialog,
      itemForm,
      itemValid,
      isSaving,
      showDescription,
      editItemData,
      selectedGroup,
      newGroupName,
      quantityRules,
      groupOptions,
      saveItem,
      cancelEdit
    }
  }
}
</script>